import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { FileText, Plus, History, ArrowRight, Clock, Share2, MessageSquare, Search, TrendingUp, Sparkles, Copy, Check, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { UsageCard } from '../components/dashboard/UsageCard';
import { DiscoveryFeed } from '../components/discovery/DiscoveryFeed';
import { getRecentTranscriptions, getTrendingTranscriptions } from '../lib/api';
import { TranscriptionResponse } from '../lib/types';
import { TranscriptionCard } from '../components/transcription/TranscriptionCard';
import { Input } from '../components/ui/input';
export function Dashboard() {
  const {
    user,
    session
  } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const [transcriptions, setTranscriptions] = useState<TranscriptionResponse[]>([]);
  const [activeTab, setActiveTab] = useState<'recent' | 'trending'>('recent');
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [copied, setCopied] = useState(false);
  const phoneNumber = '(774) 472-7423';
  const smsHref = `sms:+1${phoneNumber.replace(/\D/g, '')}?body=Hi%20ScribeTok!%20I%20want%20to%20transcribe%20a%20video.`;
  // Show personal dashboard for authenticated users
  if (session) {
    return <div className="container py-4 md:py-8 px-4 md:px-6" data-id="element-2062">
        <div className="mb-6 md:mb-8" data-id="element-2063">
          <h1 className="text-2xl md:text-3xl font-bold" data-id="element-2064">Dashboard</h1>
          <p className="text-muted-foreground mt-2" data-id="element-2065">
            Welcome back{user?.email ? `, ${user.email.split('@')[0]}` : ''}!
            Manage your transcriptions and account.
          </p>
        </div>

        {/* Usage Card with Pricing */}
        <UsageCard data-id="element-2066" />

        {/* Quick Actions */}
        <div className="mb-8" data-id="element-2067">
          <h2 className="text-xl font-semibold mb-4" data-id="element-2068">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-id="element-2069">
            <Link to="/app/transcriptions" data-id="element-2070">
              <Card className="h-full hover:border-primary/50 hover:shadow-sm transition-all" data-id="element-2071">
                <CardContent className="p-6 flex flex-col h-full" data-id="element-2072">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-4" data-id="element-2073">
                    <FileText size={18} className="text-primary" data-id="element-2074" />
                  </div>
                  <h3 className="font-medium mb-2" data-id="element-2075">View Transcriptions</h3>
                  <p className="text-sm text-muted-foreground mb-4 flex-grow" data-id="element-2076">
                    Browse all your saved transcriptions
                  </p>
                  <Button variant="outline" className="mt-auto w-full justify-between" data-id="element-2077">
                    View All
                    <ArrowRight size={16} data-id="element-2078" />
                  </Button>
                </CardContent>
              </Card>
            </Link>
            <Link to="/app/transcriptions" data-id="element-2079">
              <Card className="h-full hover:border-primary/50 hover:shadow-sm transition-all" data-id="element-2080">
                <CardContent className="p-6 flex flex-col h-full" data-id="element-2081">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-4" data-id="element-2082">
                    <Plus size={18} className="text-primary" data-id="element-2083" />
                  </div>
                  <h3 className="font-medium mb-2" data-id="element-2084">New Transcription</h3>
                  <p className="text-sm text-muted-foreground mb-4 flex-grow" data-id="element-2085">
                    Transcribe a new TikTok or YouTube video
                  </p>
                  <Button className="mt-auto w-full justify-between" data-id="element-2086">
                    Start New
                    <ArrowRight size={16} data-id="element-2087" />
                  </Button>
                </CardContent>
              </Card>
            </Link>
            <Link to="/app/history" data-id="element-2088">
              <Card className="h-full hover:border-primary/50 hover:shadow-sm transition-all" data-id="element-2089">
                <CardContent className="p-6 flex flex-col h-full" data-id="element-2090">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-4" data-id="element-2091">
                    <History size={18} className="text-primary" data-id="element-2092" />
                  </div>
                  <h3 className="font-medium mb-2" data-id="element-2093">Recent Activity</h3>
                  <p className="text-sm text-muted-foreground mb-4 flex-grow" data-id="element-2094">
                    View your recent transcription history
                  </p>
                  <Button variant="outline" className="mt-auto w-full justify-between" data-id="element-2095">
                    View History
                    <ArrowRight size={16} data-id="element-2096" />
                  </Button>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>

        {/* SMS Instructions */}
        <div className="mb-8" data-id="element-2097">
          <h2 className="text-xl font-semibold mb-4" data-id="element-2098">Text to Transcribe</h2>
          <Card className="bg-secondary/20" data-id="element-2099">
            <CardContent className="p-6" data-id="element-2100">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6" data-id="element-2101">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0" data-id="element-2102">
                  <MessageSquare size={20} className="text-primary" data-id="element-2103" />
                </div>
                <div className="flex-grow" data-id="element-2104">
                  <h3 className="font-medium mb-1" data-id="element-2105">
                    Text Any TikTok URL to (774) 472-7423
                  </h3>
                  <p className="text-sm text-muted-foreground" data-id="element-2106">
                    The fastest way to transcribe videos - just text the URL to
                    our number and get an instant transcript.
                  </p>
                </div>
                <div className="flex-shrink-0 mt-4 md:mt-0" data-id="element-2107">
                  <Button variant="outline" className="flex items-center" data-id="element-2108">
                    <Share2 size={16} className="mr-2" data-id="element-2109" />
                    Share Number
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Transcriptions Teaser */}
        <div data-id="element-2110">
          <div className="flex justify-between items-center mb-4" data-id="element-2111">
            <h2 className="text-xl font-semibold" data-id="element-2112">Recent Transcriptions</h2>
            <Link to="/app/transcriptions" data-id="element-2113">
              <Button variant="ghost" size="sm" className="text-primary" data-id="element-2114">
                View All
                <ArrowRight size={16} className="ml-1" data-id="element-2115" />
              </Button>
            </Link>
          </div>
          <div className="text-center py-8 bg-secondary/20 rounded-lg border border-border/40" data-id="element-2116">
            <Clock size={24} className="mx-auto mb-3 text-muted-foreground" data-id="element-2117" />
            <p className="text-muted-foreground" data-id="element-2118">
              Your recent transcriptions will appear here
            </p>
            <Link to="/app/transcriptions" data-id="element-2119">
              <Button className="mt-4" data-id="element-2120">Start Transcribing</Button>
            </Link>
          </div>
        </div>
      </div>;
  }
  // For non-authenticated users, show discovery feed with fallback
  const handleCopyNumber = () => {
    navigator.clipboard.writeText(phoneNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  useEffect(() => {
    const fetchDiscoveryContent = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const [recent, trending] = await Promise.all([getRecentTranscriptions(undefined, 12), getTrendingTranscriptions('week', undefined, 8)]);
        setTranscriptions(activeTab === 'recent' ? recent : trending);
      } catch (err) {
        console.error('Error fetching discovery content:', err);
        setError('api_unavailable');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDiscoveryContent();
  }, [activeTab]);
  const filteredTranscriptions = transcriptions.filter(item => {
    if (!searchTerm.trim()) return true;
    return item.title?.toLowerCase().includes(searchTerm.toLowerCase()) || item.video_id?.toLowerCase().includes(searchTerm.toLowerCase());
  });
  return <div className="container py-4 md:py-8 px-4 md:px-6" data-id="element-2121">
      {/* SMS CTA Banner */}
      <Card className="mb-6 overflow-hidden border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10" data-id="element-2122">
        <CardContent className="p-6" data-id="element-2123">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-2124">
            <div className="flex-1 text-center md:text-left" data-id="element-2125">
              <h2 className="text-xl md:text-2xl font-bold mb-2 flex items-center justify-center md:justify-start gap-2" data-id="element-2126">
                <Sparkles size={24} className="text-primary" data-id="element-2127" />
                Try ScribeTok via SMS
              </h2>
              <p className="text-muted-foreground mb-3" data-id="element-2128">
                Text any TikTok or YouTube URL to get an instant transcript. No
                account needed.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-3" data-id="element-2129">
                <div className="flex items-center gap-2 px-4 py-2 bg-background rounded-full cursor-pointer hover:bg-secondary/50 transition-colors" onClick={handleCopyNumber} data-id="element-2130">
                  <MessageSquare size={18} className="text-primary" data-id="element-2131" />
                  <span className="font-medium" data-id="element-2132">{phoneNumber}</span>
                  {copied ? <Check size={16} className="text-green-500" data-id="element-2133" /> : <Copy size={16} className="text-muted-foreground" data-id="element-2134" />}
                </div>
                <a href={smsHref} data-id="element-2135">
                  <Button className="w-full sm:w-auto" data-id="element-2136">
                    <MessageSquare size={16} className="mr-2" data-id="element-2137" />
                    Text Now
                  </Button>
                </a>
              </div>
            </div>
            <div className="flex-shrink-0" data-id="element-2138">
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=sms:+17744727423" alt="SMS QR Code" className="rounded-lg shadow-md" width={120} height={120} data-id="element-2139" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6" data-id="element-2140">
        <div data-id="element-2141">
          <h1 className="text-2xl md:text-3xl font-bold mb-1" data-id="element-2142">
            Discover Transcripts
          </h1>
          <p className="text-muted-foreground" data-id="element-2143">
            Browse transcriptions from the ScribeTok community
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="mb-6" data-id="element-2144">
        <CardContent className="p-4" data-id="element-2145">
          <div className="flex flex-col md:flex-row gap-4" data-id="element-2146">
            <div className="relative flex-1" data-id="element-2147">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" data-id="element-2148" />
              <Input className="pl-10" placeholder="Search transcriptions..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2149" />
            </div>
            <div className="flex gap-2" data-id="element-2150">
              <Button variant={activeTab === 'recent' ? 'default' : 'outline'} size="sm" onClick={() => setActiveTab('recent')} className="whitespace-nowrap" data-id="element-2151">
                <Clock size={16} className="mr-2" data-id="element-2152" />
                Recent
              </Button>
              <Button variant={activeTab === 'trending' ? 'default' : 'outline'} size="sm" onClick={() => setActiveTab('trending')} className="whitespace-nowrap" data-id="element-2153">
                <TrendingUp size={16} className="mr-2" data-id="element-2154" />
                Trending
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content Grid */}
      {isLoading ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6" data-id="element-2155">
          {[...Array(8)].map((_, i) => <Card key={i} className="h-full" data-id="element-2156">
              <div className="h-40 bg-secondary/40 animate-pulse rounded-t-lg" data-id="element-2157"></div>
              <CardContent className="p-4" data-id="element-2158">
                <div className="h-4 bg-secondary/40 animate-pulse rounded mb-2" data-id="element-2159"></div>
                <div className="h-4 bg-secondary/40 animate-pulse rounded w-3/4" data-id="element-2160"></div>
              </CardContent>
            </Card>)}
        </div> : error === 'api_unavailable' ? <Card className="border-primary/20 bg-gradient-to-br from-background to-primary/5" data-id="element-2161">
          <CardContent className="p-12 text-center" data-id="element-2162">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4" data-id="element-2163">
              <MessageSquare size={32} className="text-primary" data-id="element-2164" />
            </div>
            <h3 className="text-2xl font-bold mb-3" data-id="element-2165">ScribeTok is SMS-First</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto" data-id="element-2166">
              The web discovery feed requires API access. But the real magic
              happens via SMS! Text any video URL to {phoneNumber} and get
              instant transcripts.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mb-6" data-id="element-2167">
              <a href={smsHref} className="w-full sm:w-auto" data-id="element-2168">
                <Button size="lg" className="w-full" data-id="element-2169">
                  <MessageSquare size={18} className="mr-2" data-id="element-2170" />
                  Text to Transcribe
                </Button>
              </a>
              <Link to="/app/transcriptions" data-id="element-2171">
                <Button variant="outline" size="lg" className="w-full sm:w-auto" data-id="element-2172">
                  <FileText size={18} className="mr-2" data-id="element-2173" />
                  Browse Transcriptions
                </Button>
              </Link>
            </div>
            <div className="text-sm text-muted-foreground" data-id="element-2174">
              <p className="mb-2" data-id="element-2175">✨ No account needed</p>
              <p className="mb-2" data-id="element-2176">📱 Works via SMS instantly</p>
              <p data-id="element-2177">🔄 History saved to your phone number</p>
            </div>
          </CardContent>
        </Card> : filteredTranscriptions.length > 0 ? <>
          {searchTerm.trim() && <div className="mb-4 text-sm text-muted-foreground" data-id="element-2178">
              Found {filteredTranscriptions.length} results for "{searchTerm}"
            </div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6" data-id="element-2179">
            {filteredTranscriptions.map(transcription => <TranscriptionCard key={transcription.task_id} transcription={transcription} data-id="element-2180" />)}
          </div>
        </> : <Card data-id="element-2181">
          <CardContent className="p-12 text-center" data-id="element-2182">
            <div className="text-muted-foreground mb-4" data-id="element-2183">
              <Search size={48} className="mx-auto" data-id="element-2184" />
            </div>
            <h3 className="text-lg font-medium mb-2" data-id="element-2185">
              {searchTerm.trim() ? 'No results found' : 'No transcriptions yet'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4" data-id="element-2186">
              {searchTerm.trim() ? 'Try a different search term' : 'Be the first to transcribe a video!'}
            </p>
            <a href={smsHref} data-id="element-2187">
              <Button data-id="element-2188">
                <MessageSquare size={16} className="mr-2" data-id="element-2189" />
                Text to Transcribe
              </Button>
            </a>
          </CardContent>
        </Card>}

      {/* Bottom CTA */}
      {!isLoading && !error && filteredTranscriptions.length > 0 && <Card className="mt-8 border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10" data-id="element-2190">
          <CardContent className="p-6 text-center" data-id="element-2191">
            <h3 className="text-xl font-bold mb-2" data-id="element-2192">
              Want to transcribe your own videos?
            </h3>
            <p className="text-muted-foreground mb-4" data-id="element-2193">
              Text any TikTok or YouTube URL to {phoneNumber} and get instant
              results
            </p>
            <a href={smsHref} data-id="element-2194">
              <Button size="lg" data-id="element-2195">
                <MessageSquare size={18} className="mr-2" data-id="element-2196" />
                Start Transcribing via SMS
              </Button>
            </a>
          </CardContent>
        </Card>}
    </div>;
}